import React, { useState, useEffect, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import { BrandingContext } from "../BrandingProvider";
import { usePermissions } from "./login/PermissionContext";
import { generateDepositReceiptPdf } from "./ipd-billing/utils/billingPdf";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import { getActiveCashCounterSession } from "./ipd-billing/useCashCounter";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import PdfPreviewModal from "./ipd-billing/components/PdfPreviewModal";
import {
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
  listDeposits,
  getDepositTransactionHistory,
  createDeposit,
  getDepositReceipt,
  refundDeposit,
  adjustDepositsRefunds,
  listInvoices,
  mapDepositsToWallet,
  mapDepositToTransaction,
  pickPrimaryDeposit,
  PAYMENT_MODE_TO_API,
  CASH_COUNTER_CLOSED_HINT,
} from "./ipd-billing/billing-api";

const EMPTY_WALLET = {
  available_balance: 0,
  total_deposits: 0,
  adjusted: 0,
};

// ── LOOKUP MAPS ───────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  Cleared:   { bg: "#d1f2db", color: "#145a32", border: "#badbcc" },
  Pending:   { bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
  Failed:    { bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
  Initiated: { bg: "#d1f2db", color: "#145a32", border: "#badbcc" },
  Processed: { bg: "#d1f2db", color: "#145a32", border: "#badbcc" },
  Rejected:  { bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
  REFUNDED:  { bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
  Refunded:  { bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
  Adjusted:  { bg: "#e7eefc", color: "#1c3f94", border: "#c7d7f5" },
};

const PAYMENT_MODES = ["UPI", "Card", "Cash", "Bank Transfer"];

// ── ADJUST DEPOSIT MODAL ──────────────────────────────────────────────────────
function AdjustDepositModal({ onClose, onSubmit, wallet, invoices, busy }) {
  const outstandingInvoices = invoices.filter((i) =>
    ["ISSUED", "PARTIALLY_PAID"].includes(i.invoice_status)
  );
  const [invoiceId, setInvoiceId] = useState(
    outstandingInvoices[0]?.id || outstandingInvoices[0]?.invoice_id || ""
  );
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("Apply wallet to bill");
  const [error, setError] = useState("");

  const selected = outstandingInvoices.find(
    (i) => (i.id || i.invoice_id) === invoiceId
  );
  const maxAmt = Number(selected?.outstanding_amount || 0);
  const avail = Number(wallet?.available_balance || 0);

  const handleSubmit = () => {
    const amt = Number(amount);
    if (!invoiceId) { setError("Select an invoice."); return; }
    if (!amt || amt <= 0) { setError("Enter a valid amount."); return; }
    if (amt > avail + 0.01) { setError(`Amount exceeds available balance ${fmtINR(avail)}.`); return; }
    if (amt > maxAmt + 0.01) { setError(`Amount exceeds invoice outstanding ${fmtINR(maxAmt)}.`); return; }
    onSubmit({ invoiceId, amount: amt.toFixed(2), remarks });
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "460px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Apply Deposit to Bill</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>Available: {fmtINR(avail)}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Invoice</label>
            <select value={invoiceId} onChange={(e) => { setInvoiceId(e.target.value); setAmount(""); setError(""); }} style={inputStyle}>
              {outstandingInvoices.length === 0
                ? <option value="">No outstanding invoices</option>
                : outstandingInvoices.map((inv) => (
                    <option key={inv.id || inv.invoice_id} value={inv.id || inv.invoice_id}>
                      {inv.invoice_number} · outstanding {fmtINR(inv.outstanding_amount)}
                    </option>
                  ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Amount (₹)</label>
            <input type="number" min="0" step="0.01" value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(""); }}
              style={{ ...inputStyle, fontSize: "18px", fontWeight: "700" }} />
          </div>
          <div>
            <label style={labelStyle}>Remarks</label>
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} style={inputStyle} />
          </div>
          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button onClick={onClose} disabled={busy} style={{ flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={busy || outstandingInvoices.length === 0}
            style={{ flex: 2, padding: "11px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer" }}
          >{busy ? "Applying…" : "Apply to Bill"}</button>
        </div>
      </div>
    </div>
  );
}

// ── COLLECT DEPOSIT MODAL ─────────────────────────────────────────────────────
function CollectDepositModal({ onClose, onCollect, encounterId, busy, serverError }) {
  const [form, setForm] = useState({ amount: "", mode: "UPI", reference: "", collected_by: "", notes: "" });
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!form.amount || Number(form.amount) <= 0) { setError("Enter a valid amount."); return; }
    onCollect({
      amount:       Number(form.amount),
      mode:         form.mode,
      reference:    form.reference,
      collected_by: form.collected_by || "Front Desk",
      notes:        form.notes,
    });
  };

  const inputStyle  = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelStyle  = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "460px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
          <div>
            <h3 style={{ margin: "0 0 2px", fontSize: "17px", fontWeight: "800", color: C.text }}>Collect Deposit</h3>
            <p style={{ margin: 0, fontSize: "12px", color: C.textSub }}>Encounter {encounterId}</p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px" }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Amount (₹) *</label>
            <input
              type="number" min="1"
              value={form.amount}
              onChange={e => { setForm(p => ({ ...p, amount: e.target.value })); setError(""); }}
              style={{ ...inputStyle, fontSize: "20px", fontWeight: "700", padding: "11px 14px" }}
            />
          </div>

          <div>
            <label style={labelStyle}>Payment Mode</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {PAYMENT_MODES.map(m => {
                const active = form.mode === m;
                return (
                  <button key={m} onClick={() => setForm(p => ({ ...p, mode: m }))}
                    style={{ flex: 1, padding: "9px 6px", borderRadius: "8px", border: `1.5px solid ${active ? C.primary : C.border}`, background: active ? C.primary : "#fff", color: active ? "#fff" : C.text, fontSize: "12px", fontWeight: "600", cursor: "pointer" }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            {/* Cash carries no instrument reference, so it is not required for that mode. */}
            <label style={labelStyle}>{form.mode === "Cash" ? "Reference" : "Reference *"}</label>
            <input
              value={form.reference}
              onChange={e => { setForm(p => ({ ...p, reference: e.target.value })); setError(""); }}
              placeholder={form.mode === "Cash" ? "Optional" : "UTR / last 4 digits / receipt no."}
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Collected By</label>
              <input value={form.collected_by} onChange={e => setForm(p => ({ ...p, collected_by: e.target.value }))} placeholder="e.g. Front Desk" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Notes</label>
              <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional" style={inputStyle} />
            </div>
          </div>

          {/* The server's refusal belongs in front of the cashier, not on the page behind
              this dialog — a shut cash counter is answered here. */}
          {(error || serverError) && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error || serverError}</div>}
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={busy} style={{ flex: 2, padding: "11px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer" }}>{busy ? "Collecting…" : "Collect Deposit"}</button>
        </div>
      </div>
    </div>
  );
}

// ── NEW REFUND MODAL ──────────────────────────────────────────────────────────
function NewRefundModal({ onClose, onSubmit, encounterId, maxAmount, busy }) {
  const [form, setForm] = useState({ amount: "", mode: "UPI", patient: "", reason: "", ref_detail: "" });
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!form.amount || Number(form.amount) <= 0) { setError("Enter a valid refund amount."); return; }
    if (Number(form.amount) > maxAmount + 0.01) {
      setError(`Refund cannot exceed available balance ${fmtINR(maxAmount)}.`);
      return;
    }
    if (!form.reason) { setError("Reason is required."); return; }
    onSubmit({
      amount:  Number(form.amount),
      mode:    form.mode,
      patient: form.patient || "Patient",
      reason:  form.reason,
      ref_detail: form.ref_detail,
    });
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: `1px solid ${C.border}`, borderRadius: "7px", fontSize: "13px", color: C.text, outline: "none", boxSizing: "border-box", background: "#fff" };
  const labelStyle = { fontSize: "11px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: "5px" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>Refund Unused Deposit</h3>
            <p style={{ margin: "4px 0 0", fontSize: "12px", color: C.textSub }}>Available to refund: {fmtINR(maxAmount)}</p>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", padding: "4px" }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Refund Amount (₹) *</label>
            <input type="number" min="1" value={form.amount} onChange={e => { setForm(p => ({ ...p, amount: e.target.value })); setError(""); }}
              style={{ ...inputStyle, fontSize: "20px", fontWeight: "700", padding: "11px 14px" }} />
          </div>

          <div>
            <label style={labelStyle}>Refund Mode</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {PAYMENT_MODES.map(m => {
                const active = form.mode === m;
                return (
                  <button key={m} onClick={() => setForm(p => ({ ...p, mode: m }))}
                    style={{ flex: 1, padding: "8px 4px", borderRadius: "8px", border: `1.5px solid ${active ? C.primary : C.border}`, background: active ? C.primary : "#fff", color: active ? "#fff" : C.text, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={labelStyle}>Patient Name</label>
            <input value={form.patient} onChange={e => setForm(p => ({ ...p, patient: e.target.value }))} placeholder="Patient name" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Reason *</label>
            <input value={form.reason} onChange={e => { setForm(p => ({ ...p, reason: e.target.value })); setError(""); }} placeholder="Reason for refund" style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>{form.mode === "UPI" ? "UPI ID" : form.mode === "Card" ? "Card Last 4 Digits" : form.mode === "Bank Transfer" ? "Account / IFSC" : "Receipt Reference"}</label>
            <input value={form.ref_detail} onChange={e => setForm(p => ({ ...p, ref_detail: e.target.value }))} placeholder="Payment details" style={inputStyle} />
          </div>

          {error && <div style={{ fontSize: "12px", color: C.danger, fontWeight: "600" }}>{error}</div>}
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={busy} style={{ flex: 2, padding: "11px", background: C.primary, color: "#fff", border: "none", borderRadius: "8px", fontSize: "14px", fontWeight: "700", cursor: busy ? "wait" : "pointer" }}>{busy ? "Processing…" : "Submit Refund"}</button>
        </div>
      </div>
    </div>
  );
}

// ── PRINT LETTERHEAD MODAL ────────────────────────────────────────────────────
// Same choice the bill print offers: print on blank paper (facility header/footer
// drawn) or on pre-printed stationery (bands withheld, layout unchanged).
const PRINT_LETTERHEAD_OPTIONS = [
  { k: "WITH", l: "With letterhead", d: "Facility header and footer band on every page" },
  { k: "WITHOUT", l: "Without letterhead", d: "For printing on pre-printed stationery" },
];

function PrintLetterheadModal({ onClose, onSubmit, busy }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: "16px", padding: "28px", width: "440px", maxWidth: "92vw", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 6px", fontSize: "17px", fontWeight: "800", color: C.text }}>Print Receipt</h3>
        <p style={{ margin: "0 0 18px", fontSize: "12px", color: C.textSub }}>Print with or without the letterhead?</p>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {PRINT_LETTERHEAD_OPTIONS.map(opt => (
            <button
              key={opt.k}
              type="button"
              onClick={() => onSubmit(opt.k)}
              disabled={busy}
              style={{ display: "block", width: "100%", textAlign: "left", cursor: busy ? "wait" : "pointer", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "10px", padding: "12px 14px", font: "inherit" }}
            >
              <span style={{ display: "block", fontSize: "14px", fontWeight: "700", color: C.text }}>{opt.l}</span>
              <span style={{ display: "block", marginTop: "2px", fontSize: "12px", color: C.textSub }}>{opt.d}</span>
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
          <button onClick={onClose} disabled={busy} style={{ flex: 1, padding: "11px", background: "#fff", color: C.textSub, border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "14px", fontWeight: "600", cursor: "pointer" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── SKELETON ──────────────────────────────────────────────────────────────────
const shimmerStyle = {
  background: "linear-gradient(90deg,#e9ecef 25%,#f8f9fa 50%,#e9ecef 75%)",
  backgroundSize: "200% 100%",
  animation: "ipdShimmer 1.4s infinite",
  borderRadius: "4px",
};

// ── PAGE ──────────────────────────────────────────────────────────────────────
export default function IPDBillingDeposits() {
  const location      = useLocation();
  const passedPatient = location.state?.patient || null;

  const [activeAdmissionId, setActiveAdmissionId] = useState(() => resolveAdmissionId(passedPatient));
  const [selectedPatient, setSelectedPatient] = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions, setAdmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [wallet, setWallet] = useState(EMPTY_WALLET);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [primaryDeposit, setPrimaryDeposit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [showCollect, setShowCollect] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null); // { doc, filename, subtitle }
  const [printDepositId, setPrintDepositId] = useState(null); // deposit awaiting the letterhead choice
  // Receipt print: `lastDeposit` drives the post-collection "Print Receipt" button,
  // which fetches the receipt and renders a jsPDF + autoTable document.
  const [lastDeposit, setLastDeposit] = useState(null);
  const brandingData = useContext(BrandingContext);
  // Facility address / statutory details for the printed letterhead.
  const { userInfo } = usePermissions();

  const encounterId = selectedPatient?.encounter_id || activeAdmissionId || "—";
  const hasPatient = Boolean(activeAdmissionId);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      const list = await listAllocatedAdmissions("Allocated");
      setAdmissions(list);
    } catch (err) {
      console.error("Admissions fetch error:", err);
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => {
    loadAdmissions();
  }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setActiveAdmissionId(id);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const clearDepositData = useCallback(() => {
    setWallet(EMPTY_WALLET);
    setTransactions([]);
    setInvoices([]);
    setPrimaryDeposit(null);
    setLastDeposit(null);
  }, []);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setActiveAdmissionId(String(row.admission_id));
    setSearchQuery("");
    setApiError("");
    setApiMessage("");
    setLastDeposit(null);
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    setActiveAdmissionId("");
    clearAdmissionContext();
    setSearchQuery("");
    clearDepositData();
    setApiError("");
    setApiMessage("Search and select a patient to manage deposits.");
  }, [clearDepositData]);

  const refreshData = useCallback(async () => {
    if (!activeAdmissionId) {
      clearDepositData();
      return;
    }
    setIsLoading(true);
    setApiError("");
    try {
      const [depositList, invoiceList, transactionHistory] =
        await Promise.all([
          listDeposits(activeAdmissionId),
          listInvoices(activeAdmissionId).catch(() => []),
          getDepositTransactionHistory(activeAdmissionId).catch(() => []),
        ]);
      const walletData = mapDepositsToWallet(depositList);
      setWallet(walletData);
      setTransactions(
        (transactionHistory.length ? transactionHistory : depositList).map(
          mapDepositToTransaction
        )
      );
      setInvoices(invoiceList);
      setPrimaryDeposit(pickPrimaryDeposit(depositList));
    } catch (err) {
      console.error("Deposits fetch error:", err);
      setApiError(err.message || "Failed to load deposits");
      clearDepositData();
    } finally {
      setIsLoading(false);
    }
  }, [activeAdmissionId, clearDepositData]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const handlePrintReceipt = useCallback(
    async (depositId, letterhead) => {
      if (!activeAdmissionId || !depositId) return;
      setBusy(true);
      setApiError("");
      try {
        const receipt = await getDepositReceipt(activeAdmissionId, depositId);
        const doc = generateDepositReceiptPdf({
          receipt, brandingData, userInfo, letterhead: letterhead !== "WITHOUT",
        });
        setPdfPreview({
          doc,
          filename: `Receipt-${receipt?.deposit?.receipt_no || depositId}.pdf`,
          subtitle: receipt?.deposit?.receipt_no || "",
        });
      } catch (err) {
        setApiError(err.message || "Could not load receipt");
      } finally {
        setBusy(false);
        setPrintDepositId(null);
      }
    },
    [activeAdmissionId, brandingData, userInfo]
  );

  const handleCollect = async (form) => {
    if (!activeAdmissionId) return;
    setBusy(true);
    setApiError("");
    try {
      // Which counter this cash is being taken at, so it reaches that counter's sheet.
      // A payment has always carried this; a deposit is the same money at the same window.
      const activeCounter = getActiveCashCounterSession();
      const created = await createDeposit(activeAdmissionId, {
        deposit_amount: form.amount.toFixed(2),
        payment_mode: PAYMENT_MODE_TO_API[form.mode] || form.mode,
        transaction_reference: form.reference || undefined,
        collected_by: form.collected_by || undefined,
        remarks: form.notes || "Admission advance",
        ...(activeCounter && {
          cash_counter_session_id: activeCounter.sessionId,
          counter_code: activeCounter.counterCode,
        }),
      });
      const depositId =
        created?.deposit_id || created?.deposit?.id || created?.id || null;
      const depositNo =
        created?.deposit_number || created?.deposit?.deposit_number || null;
      setLastDeposit(depositId ? { id: depositId, number: depositNo } : null);
      setApiMessage("Deposit collected.");
      setShowCollect(false);
      await refreshData();
    } catch (err) {
      setApiError(err.cashCounterRequired ? CASH_COUNTER_CLOSED_HINT : (err.message || "Deposit collection failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async (form) => {
    if (!activeAdmissionId || !primaryDeposit) {
      setApiError("No active deposit available for refund.");
      return;
    }
    const depositId = primaryDeposit.id || primaryDeposit.deposit_id;
    setBusy(true);
    setApiError("");
    try {
      await refundDeposit(activeAdmissionId, depositId, {
        amount: form.amount.toFixed(2),
        payment_mode: PAYMENT_MODE_TO_API[form.mode] || form.mode,
        transaction_reference: form.ref_detail || undefined,
        remarks: form.reason,
      });
      setApiMessage("Refund processed.");
      setShowRefund(false);
      await refreshData();
    } catch (err) {
      setApiError(err.message || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  const handleAdjust = async ({ invoiceId, amount, remarks }) => {
    if (!activeAdmissionId) return;
    setBusy(true);
    setApiError("");
    try {
      await adjustDepositsRefunds(activeAdmissionId, {
        adjustment_type: "apply_to_bill",
        allocations: [{ invoice_id: invoiceId, amount }],
        remarks,
      });
      setApiMessage("Deposit applied to invoice.");
      setShowAdjust(false);
      await refreshData();
    } catch (err) {
      setApiError(err.message || "Allocation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <IPDBillingLayout activeNav="deposit">
      <style>{`.tx-row:hover { background: #f8f9fa !important; }`}</style>

      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to manage deposits & refunds"
        />

        {/* ── PAGE TITLE + ACTION ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Deposits & Refunds</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              {hasPatient
                ? `Wallet for ${selectedPatient?.name || "patient"} · Admission ${activeAdmissionId}`
                : "Select a patient above to view wallet and transactions"}
            </p>
            {apiError && <p style={{ margin: "6px 0 0", fontSize: "12px", color: C.danger, fontWeight: "600" }}>{apiError}</p>}
            {apiMessage && !apiError && (
              <div style={{ margin: "6px 0 0", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <span style={{ fontSize: "12px", color: C.success, fontWeight: "600" }}>{apiMessage}</span>
                {lastDeposit && (
                  <button
                    onClick={() => setPrintDepositId(lastDeposit.id)}
                    disabled={busy}
                    style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "6px 14px", background: "#fff", color: C.primary, border: `1.5px solid ${C.primary}`, borderRadius: "7px", fontSize: "12px", fontWeight: "700", cursor: busy ? "wait" : "pointer" }}
                  >
                    <SvgIcon name="printer" size={14} color={C.primary} />
                    Print Receipt{lastDeposit.number ? ` · ${lastDeposit.number}` : ""}
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            onClick={() => { if (hasPatient) { setApiError(""); setShowCollect(true); } }}
            disabled={!hasPatient || busy}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 22px", background: C.primary, color: "#fff", border: "none", borderRadius: "9px", fontSize: "14px", fontWeight: "700", cursor: hasPatient ? "pointer" : "not-allowed", opacity: hasPatient ? 1 : 0.55, boxShadow: "0 3px 10px rgba(13,35,63,0.25)", transition: "background 0.15s" }}
            onMouseEnter={e => { if (hasPatient) e.currentTarget.style.background = C.primaryHover; }}
            onMouseLeave={e => { if (hasPatient) e.currentTarget.style.background = C.primary; }}
          >
            <SvgIcon name="plus" size={16} color="#fff" /> Collect Deposit
          </button>
        </div>

        {!hasPatient ? (
          <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, padding: "48px 32px", textAlign: "center" }}>
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "8px" }}>No patient selected</div>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub, lineHeight: 1.6 }}>
              Search and select an allocated IPD patient to collect deposits, apply wallet balance, or process refunds.
            </p>
          </div>
        ) : (
        /* ── TWO-COLUMN LAYOUT ── */
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>

          {/* ── LEFT PANEL ── */}
          <div style={{ width: "380px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "16px" }}>

            {/* Encounter Wallet Card */}
            <div style={{ background: "linear-gradient(145deg, #1a56aa 0%, #0d233f 100%)", borderRadius: "14px", padding: "22px 24px", color: "#fff" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px", opacity: 0.85 }}>
                <SvgIcon name="wallet" size={16} color="#fff" />
                <span style={{ fontSize: "13px", fontWeight: "600", letterSpacing: "0.02em" }}>Encounter Wallet</span>
              </div>

              <div style={{ fontSize: "11px", fontWeight: "600", opacity: 0.65, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Available Balance
              </div>
              <div style={{ fontSize: "40px", fontWeight: "800", marginBottom: "20px", letterSpacing: "-0.01em" }}>
                {fmtINR(wallet.available_balance)}
              </div>

              <div style={{ display: "flex", gap: "32px", marginBottom: "22px" }}>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.65, marginBottom: "3px" }}>Total Deposits</div>
                  <div style={{ fontSize: "15px", fontWeight: "700" }}>{fmtINR(wallet.total_deposits)}</div>
                </div>
                <div>
                  <div style={{ fontSize: "11px", opacity: 0.65, marginBottom: "3px" }}>Adjusted</div>
                  <div style={{ fontSize: "15px", fontWeight: "700" }}>{fmtINR(wallet.adjusted)}</div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => wallet.available_balance > 0 && setShowAdjust(true)}
                  disabled={wallet.available_balance <= 0}
                  style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: wallet.available_balance <= 0 ? "not-allowed" : "pointer", opacity: wallet.available_balance <= 0 ? 0.6 : 1, transition: "background 0.15s" }}
                  onMouseEnter={e => { if (wallet.available_balance > 0) e.currentTarget.style.background = "rgba(255,255,255,0.22)"; }}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
                >
                  Adjust
                </button>
                <button
                  onClick={() => { setApiError(""); setShowCollect(true); }}
                  style={{ flex: 1, padding: "10px", background: "#fff", color: C.primary, border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", transition: "opacity 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Raise a refund. There is deliberately no list of refunds here: a refund is
                paid out and settled in one step at the instruction screen, so it is finished
                the moment it exists. Listing them beside the transaction history — which
                already shows every refund and its status — only showed the same payout twice.
            */}
            <div style={{ background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
              <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: C.text }}>Refunds</h3>
              </div>
              <div style={{ padding: "12px 20px" }}>
                <button
                  onClick={() => setShowRefund(true)}
                  style={{ width: "100%", padding: "10px", background: "#fff", color: C.text, border: `1.5px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", transition: "all 0.15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = C.accent; e.currentTarget.style.borderColor = "#b8d4ee"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = C.border; }}
                >
                  <SvgIcon name="refund" size={14} color={C.text} /> New Refund
                </button>
              </div>
            </div>
          </div>

          {/* ── RIGHT: TRANSACTION HISTORY ── */}
          <div style={{ flex: 1, minWidth: 0, background: C.card, borderRadius: "12px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(13,35,63,0.07)" }}>
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}` }}>
              <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>Transaction History</h3>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {["RECEIPT", "DATE", "MODE", "REFERENCE", "COLLECTED BY", "AMOUNT", "STATUS"].map((h, i) => (
                      <th key={i} style={{ padding: "10px 16px", fontSize: "10px", fontWeight: "700", color: C.textSub, letterSpacing: "0.07em", textAlign: i >= 5 ? "right" : "left", whiteSpace: "nowrap", textTransform: "uppercase" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ? Array(4).fill(0).map((_, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                          {Array(7).fill(0).map((_, j) => (
                            <td key={j} style={{ padding: "16px" }}>
                              <div style={{ height: "13px", ...shimmerStyle }} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : transactions.length === 0
                      ? (
                          <tr>
                            <td colSpan="7" style={{ padding: "48px", textAlign: "center", fontSize: "14px", color: C.textSub }}>
                              {isLoading ? "Loading transactions…" : "No deposits yet. Collect the first advance to get started."}
                            </td>
                          </tr>
                        )
                      : transactions.map((row, idx) => {
                          const st = STATUS_STYLE[row.status] || STATUS_STYLE.Pending;
                          return (
                            <tr key={row.id} className="tx-row"
                              style={{ borderBottom: `1px solid ${C.border}`, background: idx % 2 === 0 ? "#fff" : "#fafbfc", transition: "background 0.15s" }}
                            >
                              <td style={{ padding: "14px 16px", fontSize: "12px", fontWeight: "700", fontFamily: "monospace", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                                {row.deposit_id ? (
                                  <button
                                    onClick={() => setPrintDepositId(row.deposit_id)}
                                    disabled={busy}
                                    title="Print receipt"
                                    style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: 0, background: "transparent", border: "none", color: C.primary, fontFamily: "monospace", fontSize: "12px", fontWeight: "700", cursor: busy ? "wait" : "pointer" }}
                                  >
                                    <SvgIcon name="printer" size={13} color={C.primary} />
                                    {row.receipt}
                                  </button>
                                ) : (
                                  <span style={{ color: C.text }}>{row.receipt}</span>
                                )}
                              </td>
                              <td style={{ padding: "14px 16px", fontSize: "13px", color: C.textSub, whiteSpace: "nowrap" }}>{row.date}</td>
                              <td style={{ padding: "14px 16px", fontSize: "13px", fontWeight: "600", color: C.text, whiteSpace: "nowrap" }}>{row.mode}</td>
                              <td style={{ padding: "14px 16px", fontSize: "13px", color: C.textSub }}>{row.reference}</td>
                              <td style={{ padding: "14px 16px", fontSize: "13px", color: C.text, fontWeight: "500" }}>{row.collected_by}</td>
                              <td style={{ padding: "14px 16px", fontSize: "13px", fontWeight: "700", color: Number(row.amount) < 0 ? (C.danger || "#c0392b") : C.text, textAlign: "right", whiteSpace: "nowrap" }}>
                                {Number(row.amount) < 0 ? `- ${fmtINR(Math.abs(Number(row.amount)))}` : fmtINR(row.amount)}
                              </td>
                              <td style={{ padding: "14px 16px", textAlign: "right" }}>
                                <span style={{ fontSize: "12px", fontWeight: "700", padding: "3px 10px", borderRadius: "100px", background: st.bg, color: st.color, border: `1px solid ${st.border}`, whiteSpace: "nowrap" }}>
                                  {row.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
        )}
      </div>

      {showCollect && (
        <CollectDepositModal
          onClose={() => setShowCollect(false)}
          onCollect={handleCollect}
          encounterId={encounterId}
          busy={busy}
          serverError={apiError}
        />
      )}
      {showRefund && (
        <NewRefundModal
          onClose={() => setShowRefund(false)}
          onSubmit={handleRefund}
          encounterId={encounterId}
          maxAmount={wallet.available_balance}
          busy={busy}
        />
      )}
      {showAdjust && (
        <AdjustDepositModal
          onClose={() => setShowAdjust(false)}
          onSubmit={handleAdjust}
          wallet={wallet}
          invoices={invoices}
          busy={busy}
        />
      )}
      {printDepositId && (
        <PrintLetterheadModal
          onClose={() => setPrintDepositId(null)}
          onSubmit={(letterhead) => handlePrintReceipt(printDepositId, letterhead)}
          busy={busy}
        />
      )}
      {pdfPreview && (
        <PdfPreviewModal
          doc={pdfPreview.doc}
          filename={pdfPreview.filename}
          title="Receipt Preview"
          subtitle={pdfPreview.subtitle}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </IPDBillingLayout>
  );
}
